<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * List container with density, separator, border, and padding options.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering a vertical list of interactive or read-only items
 * @see https://quasar.dev/vue-components/list-and-list-items Quasar q-list API
 */
defineOptions({ name: 'HrList' });

const props = defineProps({
  /** Compact spacing between items. */
  dense: {
    type: Boolean,
    default: false,
  },
  /** Adds separator lines between items. */
  separator: {
    type: Boolean,
    default: false,
  },
  /** Adds a border around the entire list. */
  bordered: {
    type: Boolean,
    default: false,
  },
  /** Adds vertical padding inside the list. */
  padding: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props passed through to q-list for backwards compatibility with the legacy
   * theme. Ignored when a design system theme is active.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    dense: props.dense,
    separator: props.separator,
    bordered: props.bordered,
    padding: props.padding,
  };
  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-list
    v-bind="effectiveProps"
    :class="hasTheme ? ['hr-list', props.dense && 'hr-list--dense'] : undefined"
  >
    <!-- @slot List items — HrItem, HrNavItem, HrNavGroup, or HrExpansionItem. -->
    <slot />
  </q-list>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Base ───────────────────────────────────────────────────────── */
/* Quasar's q-list defaults to white, which creates a visible surface against the popup's
   own background. Transparent lets the menu/page surface show through. */
.hr-list {
  background: transparent;
}

/* ── Separator ──────────────────────────────────────────────────── */
/*
 * Override Quasar's separator color (rgba(0,0,0,0.12)) with the M3
 * outline-variant token. The q-list--separator class is added by
 * Quasar to the same root element when the separator prop is true.
 */
.hr-list.q-list--separator :deep(.q-item + .q-item) {
  border-top-color: var(--app-outline-variant);
}

/* ── Bordered ───────────────────────────────────────────────────── */
.hr-list.q-list--bordered {
  border-color: var(--app-outline-variant);
  border-radius: var(--app-shape-corner-extra-small);
}

</style>
